import { Link } from "react-router-dom"
import Seo from "@/components/Seo"
import { useRef } from "react"
const teamPhoto = "/images/image.png";
const ladyPhoto = "/images/image-2.png";
const interactiveHero = "/images/interactive-hero.png";

const IMGS = {
  serverRoom:
    "/website-assets/images/photo-1558494949-ef010cbdcc31-1400x900.jpg",
  team: null, // replaced with real photo below
  itOffice:
    "/website-assets/images/photo-1606857521015-7f9fcf423740-900x600.jpg",
  woman: "/website-assets/images/photo-1573164713988-8665fc963095-700x500.jpg",
  abstract:
    "/website-assets/images/photo-1770699197193-8bab6f147108-1600x900.jpg",
}

const STATS = [
  { v: "500+", l: "Projects Delivered" },
  { v: "17+", l: "Years Experience" },
  { v: "3", l: "Global Offices" },
  { v: "98%", l: "Client Satisfaction" },
]

const SERVICES_PREVIEW = [
  {
    icon: "🌐",
    title: "Network Infrastructure",
    desc: "Enterprise-grade LAN/WAN, fiber optic, and managed connectivity.",
  },
  {
    icon: "☁️",
    title: "Cloud Solutions",
    desc: "AWS, Azure & GCP migration, architecture, and managed services.",
  },
  {
    icon: "🔒",
    title: "Cybersecurity",
    desc: "SOC ops, penetration testing, compliance, 24/7 threat monitoring.",
  },
  {
    icon: "💡",
    title: "IT Consulting",
    desc: "Digital transformation strategy and executive-level advisory.",
  },
  {
    icon: "🖥️",
    title: "Hardware Supply",
    desc: "Certified hardware procurement, installation, and lifecycle management.",
  },
  {
    icon: "🛠️",
    title: "Managed IT Services",
    desc: "Proactive monitoring, helpdesk, and full IT outsourcing.",
  },
]

const TESTIMONIALS = [
  {
    name: "David Mugisha",
    role: "CTO, Equity Bank Uganda",
    quote:
      "Jero-One transformed our entire network infrastructure. The expertise and professionalism is unmatched.",
  },
  {
    name: "Sandra Mitchell",
    role: "IT Director, Minneapolis Health Systems",
    quote:
      "Our Minnesota office has relied on Jero-One for 5 years. Best-in-class support and response time.",
  },
  {
    name: "Patrick Nkurunziza",
    role: "CEO, East Africa Telecoms",
    quote:
      "From Kigali to Kampala, Jero-One has been the backbone of our digital infrastructure.",
  },
]

const CLIENTS = [
  "Ministry of ICT Uganda",
  "Equity Bank",
  "MTN Uganda",
  "UN Agencies",
  "Standard Chartered",
  "Stanbic Bank",
]

const HERO_NODES = [
  {
    label: "Cloud",
    detail: "ACTIVE",
    position: "left-[12%] top-[18%]",
    delay: "0s",
  },
  {
    label: "Security",
    detail: "PROTECTED",
    position: "right-[10%] top-[31%]",
    delay: "0.7s",
  },
  {
    label: "Networks",
    detail: "CONNECTED",
    position: "left-[18%] bottom-[31%]",
    delay: "1.4s",
  },
  {
    label: "FortiGate NGFW",
    detail: "THREATS BLOCKED",
    position: "left-[40%] top-[9%]",
    delay: "2.1s",
    to: "/shop/secure-sdwan",
    info: "Fortinet next-gen firewalls stop ransomware & intrusions at the edge",
  },
  {
    label: "VeloCloud SD-WAN",
    detail: "BRANCHES LINKED",
    position: "right-[14%] bottom-[46%]",
    delay: "2.8s",
    to: "/shop/secure-sdwan",
    info: "Secure, self-healing links between HQ, branches & cloud apps",
  },
  {
    label: "Smart Rooms",
    detail: "4K · TEAMS · ZOOM",
    position: "left-[46%] top-[42%]",
    delay: "3.5s",
    to: "/shop/interactive-conferencing",
    info: "Interactive boards & video-conferencing rooms, installed end-to-end",
  },
]

function InteractiveHeroVisual() {
  const visualRef = useRef(null)

  const moveVisual = (event) => {
    const visual = visualRef.current
    if (!visual) return

    const bounds = visual.getBoundingClientRect()
    const x = (event.clientX - bounds.left) / bounds.width
    const y = (event.clientY - bounds.top) / bounds.height
    visual.style.setProperty("--pointer-x", `${x * 100}%`)
    visual.style.setProperty("--pointer-y", `${y * 100}%`)
    visual.style.setProperty("--tilt-x", `${(0.5 - y) * 7}deg`)
    visual.style.setProperty("--tilt-y", `${(x - 0.5) * 9}deg`)
    visual.style.setProperty("--shift-x", `${(x - 0.5) * -18}px`)
    visual.style.setProperty("--shift-y", `${(y - 0.5) * -18}px`)
  }

  const resetVisual = () => {
    const visual = visualRef.current
    if (!visual) return
    visual.style.setProperty("--pointer-x", "50%")
    visual.style.setProperty("--pointer-y", "50%")
    visual.style.setProperty("--tilt-x", "0deg")
    visual.style.setProperty("--tilt-y", "0deg")
    visual.style.setProperty("--shift-x", "0px")
    visual.style.setProperty("--shift-y", "0px")
  }

  return (
    <div
      ref={visualRef}
      onPointerMove={moveVisual}
      onPointerLeave={resetVisual}
      data-testid="interactive-hero-canvas"
      className="interactive-hero relative h-full min-h-[70vh] overflow-hidden bg-[#07100A]"
      aria-label="Interactive technology network"
    >
      <div className="interactive-hero-depth absolute inset-[-5%]">
        <img
          src={interactiveHero}
          alt="Interactive circuit board representing Jero-One's connected technology services"
          className="h-full w-full object-cover"
        />
      </div>
      <div className="interactive-hero-grid absolute inset-0" />
      <div className="interactive-hero-light pointer-events-none absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F0B] via-transparent to-[#0A0F0B]/35" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F0B]/90 via-transparent to-[#0A0F0B]/30" />

      <div className="pointer-events-none absolute left-[27%] top-0 h-full w-px bg-gradient-to-b from-transparent via-[#2A7D4A]/70 to-transparent">
        <span className="hero-signal absolute left-[-2px] h-12 w-[5px] rounded-full bg-[#8FFFAE] shadow-[0_0_18px_#2A7D4A]" />
      </div>
      <div className="pointer-events-none absolute left-0 top-[63%] h-px w-full bg-gradient-to-r from-transparent via-[#2A7D4A]/60 to-transparent">
        <span className="hero-signal-horizontal absolute top-[-2px] h-[5px] w-12 rounded-full bg-[#8FFFAE] shadow-[0_0_18px_#2A7D4A]" />
      </div>

      {HERO_NODES.map((node) => (
        <Link
          key={node.label}
          to={node.to || "/services"}
          data-testid={`hero-node-${node.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={`hero-node group absolute ${node.position} z-10 rounded-xl border border-[#75E89A]/35 bg-[#07100A]/80 px-4 py-3 backdrop-blur-md transition-all hover:scale-105 hover:border-[#75E89A] hover:bg-[#1B5E35]/80`}
          style={{ animationDelay: node.delay }}
        >
          <span className="mb-1 flex items-center gap-2 text-xs font-bold text-[#F4F6F0]">
            <span className="hero-node-pulse h-2 w-2 rounded-full bg-[#75E89A]" />
            {node.label}
          </span>
          <span className="font-mono text-[9px] tracking-widest text-[#75E89A]">
            {node.detail}
          </span>
          {node.info && (
            <span className="pointer-events-none absolute left-0 top-full mt-2 w-56 rounded-lg border border-[#75E89A]/30 bg-[#07100A]/95 p-3 text-[11px] leading-snug text-[#F4F6F0]/85 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              {node.info}
            </span>
          )}
        </Link>
      ))}

      <div className="absolute left-6 top-6 z-10 flex items-center gap-3 rounded-full border border-[#75E89A]/20 bg-[#07100A]/70 px-4 py-2 font-mono text-[9px] tracking-widest text-[#75E89A] backdrop-blur">
        <span className="hero-node-pulse h-1.5 w-1.5 rounded-full bg-[#75E89A]" />
        MOVE TO EXPLORE THE NETWORK
      </div>
    </div>
  )
}

export function Home() {
  return (
    <div style={{ fontFamily: "'Barlow', sans-serif" }}>
      <Seo title="Jero-One Technologies | IT Solutions Uganda, USA & Canada" description="Enterprise IT solutions, managed services, cybersecurity, networking, CCTV and an online IT shop. Offices in Kampala/Entebbe Uganda, Minnesota USA and Canada." path="/" />
      {/* ── HERO: Split Canvas ─────────────────────────────────── */}
      <section className="grid md:grid-cols-2 min-h-[92vh]">
        {/* Left: dark text panel */}
        <div className="bg-[#0A0F0B] flex flex-col justify-center px-8 md:px-16 py-20 relative overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none opacity-5"
            style={{
              backgroundImage:
                "radial-gradient(circle at 30% 70%, #1B5E35 0%, transparent 60%)",
            }}
          />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 border border-[#1B5E35]/40 rounded-full px-4 py-1.5 text-xs text-[#2A7D4A] font-mono mb-8 tracking-widest">
              ● GLOBAL IT LEADER · EAST AFRICA AND NORTH AMERICA — USA | CANADA
            </div>
            <h1
              className="text-6xl md:text-7xl font-black text-[#F4F6F0] leading-none mb-4"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                letterSpacing: "-1px",
              }}
            >
              ENTERPRISE
              <br />
              <span style={{ color: "#1B5E35" }}>IT SOLUTIONS</span>
              <br />
              WORLDWIDE.
            </h1>
            <p className="text-[#6B7C6E] text-lg leading-relaxed mb-10 max-w-md">
              Headquartered in Minnesota, USA with franchise offices in Uganda
              and Canada — Jero-One Technologies Inc. delivers world-class IT
              infrastructure, cloud, and cybersecurity solutions globally.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/services"
                className="bg-[#1B5E35] hover:bg-[#2A7D4A] text-[#F4F6F0] font-bold px-8 py-4 rounded-xl transition-all hover:scale-105 text-sm"
              >
                Explore Services →
              </Link>
              <Link
                to="/contact"
                className="border border-[#1B5E35]/50 text-[#F4F6F0] font-semibold px-8 py-4 rounded-xl hover:border-[#2A7D4A] hover:text-[#2A7D4A] transition-colors text-sm"
              >
                Free Consultation
              </Link>
            </div>
            {/* Flags */}
            <div className="flex items-center gap-4 mt-12 pt-8 border-t border-[#1B5E35]/10">
              <span className="text-xs text-[#6B7C6E] font-mono tracking-widest">
                OFFICES IN
              </span>
              {[
                { flag: "🇺🇸", label: "USA" },
                { flag: "🇺🇬", label: "Uganda" },
                { flag: "🇨🇦", label: "Canada" },
              ].map(({ flag, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-1.5 text-sm text-[#F4F6F0]"
                >
                  <span>{flag}</span>
                  <span className="text-[#6B7C6E]">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: interactive technology image with overlaid stats */}
        <div className="relative min-h-[75vw] overflow-hidden bg-[#111A14] md:min-h-0">
          <InteractiveHeroVisual />
          {/* Stat cards */}
          <div className="pointer-events-none absolute bottom-8 left-6 right-6 z-20 grid grid-cols-2 gap-3">
            {STATS.map((s) => (
              <div
                key={s.l}
                className="bg-[#0A0F0B]/80 backdrop-blur-sm border border-[#1B5E35]/30 rounded-xl p-4 text-center"
              >
                <div
                  className="text-3xl font-black text-[#1B5E35]"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {s.v}
                </div>
                <div className="text-[10px] text-[#6B7C6E] tracking-wider mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUSTED BY ─────────────────────────────────────────── */}
      <div className="bg-[#111A14] border-y border-[#1B5E35]/10 py-6 overflow-hidden">
        <div className="flex items-center gap-3 px-8 mb-4">
          <div className="h-px w-8 bg-[#1B5E35]" />
          <span className="text-xs text-[#6B7C6E] font-mono tracking-widest">
            TRUSTED BY LEADING ORGANIZATIONS
          </span>
        </div>
        <div
          className="flex gap-12 px-8 overflow-x-auto pb-2"
          style={{ scrollbarWidth: "none" }}
        >
          {CLIENTS.map((c) => (
            <div
              key={c}
              className="flex-shrink-0 text-[#6B7C6E] text-sm font-semibold whitespace-nowrap hover:text-[#F4F6F0] transition-colors"
            >
              {c}
            </div>
          ))}
        </div>
      </div>

      {/* ── COMMERCE & CAPABILITY ──────────────────────────────── */}
      <section className="bg-[#0A0F0B] px-6 py-20 md:px-14">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 grid items-end gap-6 md:grid-cols-[1fr_auto]">
            <div>
              <div className="mb-3 font-mono text-xs tracking-widest text-[#1B5E35]">
                MORE FROM JERO-ONE
              </div>
              <h2 className="font-display text-5xl font-black leading-none text-[#F4F6F0]">
                BUY. LEARN. BUILD.
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-[#6B7C6E]">
              Source enterprise technology, train your people, and launch your
              digital presence with one accountable global partner.
            </p>
          </div>
          <div className="grid gap-5 lg:grid-cols-[1.35fr_1fr_1fr]">
            {[
              {
                to: "/shop",
                label: "ONLINE IT SHOP",
                title: "19 products ready to order",
                description:
                  "New and certified refurbished computers, networking, CCTV, software, cloud, security, peripherals, and support plans.",
                cta: "Shop products →",
                featured: true,
              },
              {
                to: "/training",
                label: "JERO-ONE ACADEMY",
                title: "Practical IT training",
                description:
                  "Eight courses for individuals and teams, with corporate, project-based, and self-paced delivery.",
                cta: "View courses →",
                featured: false,
              },
              {
                to: "/websites",
                label: "WEB SOLUTIONS",
                title: "Websites that work",
                description:
                  "DIY guidance or done-for-you websites, online shops, hosting, SEO, and ongoing support.",
                cta: "Build your website →",
                featured: false,
              },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`group rounded-2xl border p-8 transition-all hover:-translate-y-1 ${
                  item.featured
                    ? "border-[#1B5E35] bg-[#1B5E35]"
                    : "border-[#1B5E35]/15 bg-[#111A14] hover:border-[#1B5E35]/50"
                }`}
              >
                <div
                  className={`mb-8 font-mono text-[10px] tracking-widest ${
                    item.featured ? "text-[#F4F6F0]/70" : "text-[#2A7D4A]"
                  }`}
                >
                  {item.label}
                </div>
                <h3 className="font-display text-3xl font-black text-[#F4F6F0]">
                  {item.title}
                </h3>
                <p
                  className={`mt-3 min-h-24 text-sm leading-relaxed ${
                    item.featured ? "text-[#F4F6F0]/75" : "text-[#6B7C6E]"
                  }`}
                >
                  {item.description}
                </p>
                <div className="mt-6 text-sm font-bold text-[#F4F6F0]">
                  {item.cta}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES PREVIEW ───────────────────────────────────── */}
      <section className="py-24 px-6 md:px-14 bg-[#0A0F0B]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-[1fr_auto] items-end mb-14 gap-4">
            <div>
              <div className="text-xs text-[#1B5E35] font-mono tracking-widest mb-3">
                WHAT WE DO
              </div>
              <h2
                className="text-5xl font-black text-[#F4F6F0] leading-none"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                FULL-SPECTRUM
                <br />
                IT SERVICES
              </h2>
            </div>
            <Link
              to="/services"
              className="text-sm text-[#2A7D4A] font-semibold hover:underline whitespace-nowrap self-end"
            >
              All Services →
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1B5E35]/10">
            {SERVICES_PREVIEW.map((s, i) => (
              <Link
                key={i}
                to="/services"
                className="group bg-[#0A0F0B] p-8 hover:bg-[#111A14] transition-all duration-300 border border-transparent hover:border-[#1B5E35]/30"
              >
                <div className="text-3xl mb-4">{s.icon}</div>
                <h3 className="font-bold text-[#F4F6F0] text-lg mb-2 group-hover:text-[#2A7D4A] transition-colors">
                  {s.title}
                </h3>
                <p className="text-[#6B7C6E] text-sm leading-relaxed">
                  {s.desc}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT SPLIT ────────────────────────────────────────── */}
      <section className="grid md:grid-cols-2 min-h-[70vh]">
        <div className="relative overflow-hidden bg-[#111A14] min-h-64">
          <img
            src={teamPhoto}
            alt="Jero-One global team"
            className="w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to right, transparent 60%, #0A0F0B 100%)",
            }}
          />
        </div>
        <div className="bg-[#0A0F0B] flex flex-col justify-center px-8 md:px-16 py-16">
          <div className="text-xs text-[#1B5E35] font-mono tracking-widest mb-4">
            WHO WE ARE
          </div>
          <h2
            className="text-5xl font-black text-[#F4F6F0] leading-none mb-6"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            GLOBAL REACH.
            <br />
            <span style={{ color: "#1B5E35" }}>LOCAL EXPERTISE.</span>
          </h2>
          <p className="text-[#6B7C6E] leading-relaxed mb-4">
            Founded in Calgary, Alberta, Canada, Jero-One Technologies Inc.
            began with a mission to bridge the digital divide — delivering
            enterprise-grade IT solutions to businesses regardless of where they
            operate in the world.
          </p>
          <p className="text-[#6B7C6E] leading-relaxed mb-8">
            After moving to{" "}
            <strong className="text-[#F4F6F0]">Minnesota, USA</strong>, our
            headquarters now anchors our global operations, while franchise
            offices in <strong className="text-[#F4F6F0]">Uganda</strong> and{" "}
            <strong className="text-[#F4F6F0]">Canada</strong> ensure we deliver
            with deep local knowledge on every continent we serve.
          </p>
          <Link
            to="/about"
            className="self-start border border-[#1B5E35] text-[#2A7D4A] font-semibold px-7 py-3 rounded-lg hover:bg-[#1B5E35] hover:text-[#F4F6F0] transition-all text-sm"
          >
            Our Story →
          </Link>
        </div>
      </section>

      {/* ── TESTIMONIALS ───────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-14 bg-[#111A14]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs text-[#1B5E35] font-mono tracking-widest mb-3">
              CLIENT STORIES
            </div>
            <h2
              className="text-5xl font-black text-[#F4F6F0]"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              WHAT OUR CLIENTS SAY
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="bg-[#0A0F0B] border border-[#1B5E35]/10 rounded-2xl p-8 hover:border-[#1B5E35]/40 transition-colors"
              >
                <div
                  className="text-[#1B5E35] text-4xl font-black mb-4"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  "
                </div>
                <p className="text-[#F4F6F0]/80 text-sm leading-relaxed mb-6 italic">
                  {t.quote}
                </p>
                <div className="border-t border-[#1B5E35]/10 pt-4 flex items-center gap-3">
                  {i === 1 && (
                    <img
                      src={ladyPhoto}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0"
                    />
                  )}
                  <div>
                    <div className="font-bold text-[#F4F6F0] text-sm">
                      {t.name}
                    </div>
                    <div className="text-xs text-[#6B7C6E] mt-0.5">
                      {t.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ─────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <img
          src={IMGS.abstract}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-105 object-cover object-center opacity-55"
        />
        <div className="relative z-10 bg-gradient-to-r from-[#0A0F0B]/95 via-[#1B5E35]/80 to-[#0A0F0B]/80 px-6 py-20 md:px-14">
          <div className="max-w-4xl mx-auto text-center">
            <h2
              className="text-5xl md:text-6xl font-black text-[#F4F6F0] mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              READY TO TRANSFORM YOUR IT INFRASTRUCTURE?
            </h2>
            <p className="text-[#F4F6F0]/70 text-lg mb-10">
              Free consultation. No obligation. Real solutions for your
              business.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-[#F4F6F0] text-[#1B5E35] font-bold px-10 py-4 rounded-xl hover:bg-white transition-colors"
              >
                Book Free Consultation
              </Link>
              <Link
                to="/locations"
                className="border border-[#F4F6F0]/40 text-[#F4F6F0] font-semibold px-10 py-4 rounded-xl hover:border-[#F4F6F0] transition-colors"
              >
                Find Our Offices
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
